import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { api } from "../lib/api";
import { useIsAdmin } from "../lib/currentUser";
import { requireAdmin } from "../lib/requireAuth";
import { FilterSelect } from "../components/FilterSelect";
import { FilterBar, FilterEmpty } from "../components/FilterBar";
import { SearchInput } from "../components/SearchInput";
import { PageEyebrow } from "../components/PageEyebrow";
import { RoleBadge } from "../components/RoleBadge";

export const Route = createFileRoute("/admin/users")({
  beforeLoad: ({ context, location }) =>
    requireAdmin(context.queryClient, location.href),
  component: AdminUsersPage,
});

/**
 * Minimal admin users listing. Phase 1G ships the gate + the API; the
 * UI is intentionally bare so the loop doesn't blow its iteration
 * budget on chrome. S10 in `docs/auth-plan.md` polishes this + adds
 * `/admin/reports`.
 *
 * Non-admins who somehow land here see a 403 panel — the backend
 * returns 403 from `/admin/users` regardless, so this is just a
 * friendly client-side message.
 */
function AdminUsersPage() {
  const isAdmin = useIsAdmin();
  const [search, setSearch] = useState<string>("");
  const [role, setRole] = useState<string>("");
  const usersQ = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/users");
      if (error) throw error;
      return data;
    },
    // Admin pages are rarely visited; refetch every 30s feels fine.
    staleTime: 30_000,
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <section className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
        <div className="eyebrow mb-3">Admin</div>
        <h1 className="font-serif text-3xl text-ink-900 mb-4 tracking-tight">
          Admins only
        </h1>
        <p className="text-ink-500 mb-8">
          This page is restricted to users with the{" "}
          <code className="font-mono text-ink-700 bg-ink-50 border border-ink-100 px-1.5 py-0.5">
            admin
          </code>{" "}
          role.
        </p>
        <Link
          to="/home"
          className="inline-block border border-ink-200 px-5 py-3 text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors"
        >
          Back to home
        </Link>
      </section>
    );
  }

  if (usersQ.isLoading) {
    return (
      <section className="mx-auto max-w-3xl px-4 sm:px-6 py-12 text-ink-400">
        Loading users…
      </section>
    );
  }

  const users = usersQ.data ?? [];
  const needle = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (role && u.role !== role) return false;
    if (!needle) return true;
    return (
      u.name.toLowerCase().includes(needle) ||
      u.email.toLowerCase().includes(needle)
    );
  });

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <PageEyebrow
        label="Admin"
        mono="users"
        count={`${users.length} ${users.length === 1 ? "learner" : "learners"}`}
      />
      <h1 className="font-serif text-3xl text-ink-900 mb-2 tracking-tight">
        Users
      </h1>
      <p className="text-ink-500 text-sm mb-5 sm:mb-6 max-w-xl">
        Everyone on this lingua instance.
      </p>
      <div className="hairline mb-6 sm:mb-8" />

      <FilterBar
        shown={filtered.length}
        total={users.length}
        onClear={
          search || role
            ? () => {
                setSearch("");
                setRole("");
              }
            : undefined
        }
      >
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="name or email…"
        />
        <FilterSelect
          layout="inline"
          label="Role"
          value={role}
          onChange={setRole}
          options={[
            { value: "admin", label: "admin" },
            { value: "user", label: "user" },
          ]}
          placeholder="Any role"
        />
      </FilterBar>

      {filtered.length === 0 && <FilterEmpty noun="users" />}

      <ul className="divide-y divide-ink-100 border border-ink-100 bg-paper">
        {filtered.map((u) => (
          <li
            key={u.id}
            className="px-3 sm:px-4 py-3 flex items-start gap-3 hover:bg-ink-50 transition-colors"
          >
            <div className="h-8 w-8 rounded-full bg-ink-100 text-ink-700 inline-flex items-center justify-center font-serif font-medium text-sm shrink-0 select-none">
              {(u.name || u.email).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-ink-900 font-medium truncate">
                  {u.name}
                </span>
                <RoleBadge role={u.role} />
              </div>
              <div className="text-xs text-ink-400 truncate">{u.email}</div>
              <div className="text-xs text-ink-400 mt-1 tabular-nums">
                {u.attemptsCount} attempt{u.attemptsCount === 1 ? "" : "s"} ·
                joined {new Date(u.createdAt).toLocaleDateString()}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
